import {
  ConnectRepositoryRequest,
  RepositoriesResponse,
  Repository,
  UpdateBranchRequest,
  FetchRepositoriesParams,
  FetchSyncHistoryParams,
  RepositoryBranch,
  SyncHistoryResponse
} from '../types/Repository.types';
import API from './ApiBase';

export const fetchRepositories = async (
  params: FetchRepositoriesParams
): Promise<RepositoriesResponse> => {
  try {
    const queryParams = new URLSearchParams();

    // Always add offset and limit
    queryParams.append('offset', (params.offset ?? 0).toString());
    queryParams.append('limit', (params.limit ?? 10).toString());

    if (params.search) queryParams.append('keyword', params.search);
    if (params.provider && params.provider.length > 0) {
      params.provider.forEach((provider) => {
        queryParams.append('provider', provider);
      });
    }
    if (params.branch && params.branch.length > 0) {
      queryParams.append('branch', params.branch[0]);
    }
    if (params.status && params.status.length > 0) {
      params.status.forEach((status) => {
        queryParams.append('status', status);
      });
    }
    if (params.lastSyncedFrom)
      queryParams.append('lastSyncedFrom', params.lastSyncedFrom);
    if (params.lastSyncedTo)
      queryParams.append('lastSyncedTo', params.lastSyncedTo);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.order) queryParams.append('order', params.order);

    const url = `/repositories/ai-systems/${
      params.aiSystemId
    }?${queryParams.toString()}`;
    // console.log('Fetching repositories with URL:', url);
    // console.log('Query params:', Object.fromEntries(queryParams.entries()));

    const response = await API.get(url);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch repositories'
    );
  }
};

export const connectRepository = async (
  aiSystemId: string,
  requestBody: ConnectRepositoryRequest
): Promise<Repository> => {
  try {
    const response = await API.post<Repository>(
      `/ai-systems/${aiSystemId}/repositories/connect`,
      requestBody
    );
    console.log('API response - Connect repository:', response.data);
    return response.data;
  } catch (error: any) {
    console.error(
      'API error - Connect repository:',
      error.response?.data || error
    );
    throw new Error(
      error.response?.data?.message || 'Failed to connect repository'
    );
  }
};

export const deleteRepository = async (repositoryId: string): Promise<void> => {
  try {
    await API.delete(`/repositories/${repositoryId}`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to delete repository'
    );
  }
};

export const fetchRepositoryBranches = async (
  repositoryId: string
): Promise<RepositoryBranch[]> => {
  try {
    const response = await API.get(`/repositories/${repositoryId}/branches`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch repository branches'
    );
  }
};

export const fetchBranchesByAiSystem = async (
  aiSystemId: string
): Promise<string[]> => {
  try {
    const response = await API.get(
      `/repositories/ai-systems/${aiSystemId}/branches`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch branches for AI system'
    );
  }
};

export const updateRepositoryBranch = async (
  repositoryId: string,
  requestBody: UpdateBranchRequest
): Promise<void> => {
  try {
    await API.put(`/repositories/${repositoryId}/branch`, requestBody);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to update repository branch'
    );
  }
};

export const syncRepository = async (
  repositoryId: string,
  force: boolean = false
): Promise<{ message: string }> => {
  try {
    const response = await API.post(`/repositories/${repositoryId}/sync`, {
      force
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to sync repository'
    );
  }
};

export const getSyncHistory = async (
  params: FetchSyncHistoryParams
): Promise<SyncHistoryResponse> => {
  try {
    const queryParams = new URLSearchParams();

    if (params.limit !== undefined)
      queryParams.append('limit', params.limit.toString());
    if (params.offset !== undefined)
      queryParams.append('offset', params.offset.toString());
    if (params.keyword) queryParams.append('keyword', params.keyword);
    if (params.severity && params.severity.length > 0) {
      params.severity.forEach((value) => queryParams.append('severity', value));
    }
    if (params.eventType && params.eventType.length > 0) {
      params.eventType.forEach((value) =>
        queryParams.append('eventType', value)
      );
    }
    if (params.user && params.user.length > 0) {
      params.user.forEach((value) => queryParams.append('user', value));
    }
    if (params.repository && params.repository.length > 0) {
      params.repository.forEach((value) =>
        queryParams.append('repository', value)
      );
    }
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const url = `/repositories/ai-systems/${params.aiSystemId}/sync-history${
      queryString ? `?${queryString}` : ''
    }`;

    const response = await API.get<SyncHistoryResponse>(url);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch sync history'
    );
  }
};

export const authorizeRepository = async (
  repositoryId: string,
  provider: string
) => {
  // Use API.defaults.baseURL to get the correct runtime URL (from window.ENV or fallback)
  try {
    const response = await API.get<string>(
      `/auth/${provider.toLocaleLowerCase()}/authorize?repositoryId=${repositoryId}`
    );

    globalThis.location.href = response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to authorize repository'
    );
  }
};
